import { createColumnHelper } from '@tanstack/react-table';
import { groupBy } from 'lodash';
import { useMemo } from 'react';

import { AssetClass } from '../../../_openapi/portfolio-optimizer';
import { AssetClassTag } from '../../../widgets/AssetClassTag';
import {
    baseCol,
    excludeTradesCol,
    expanderCol,
    moneyCol,
    numberCol,
    textCol,
} from '../../../widgets/Table/columnHelpers/colDef';
import { CommonTradePlanEntry, getTradeAggregates, TradeTableProps } from './tradeExecutionUtils';
import { TradesTable } from './TradesTable';

interface TradePlanEntry extends CommonTradePlanEntry {
    symbol: string;
    assetClass?: AssetClass;
    price: number;
}

const columnHelper = createColumnHelper<TradePlanEntry>();
export const TradesBySecurity = ({
    secondaryGrouping,
    filteredTrades,
    excludeTrades,
    viewAsset,
    viewPortfolio,
    viewTrade,
    viewAdvanced,
}: TradeTableProps) => {
    const data: TradePlanEntry[] = useMemo(() => {
        if (!filteredTrades) {
            return [];
        }

        const grouped = groupBy(filteredTrades, (trade) => trade.asset?.symbol);
        return Object.entries(grouped).map(([symbol, trades]) => {
            return {
                symbol,
                trades,
                assetClass: trades[0].asset?.assetClass,
                price: trades[0].orderPrice,
                ...getTradeAggregates(trades),
            };
        });
    }, [filteredTrades]);

    const columns = [
        columnHelper.display(expanderCol()),
        columnHelper.accessor('symbol', textCol({ id: 'symbol' })),
        columnHelper.accessor('assetClass', {
            ...baseCol({ id: 'assetClass' }),
            cell: ({ getValue }) => <AssetClassTag assetClass={getValue()} />,
        }),
        columnHelper.accessor('price', moneyCol({ id: 'price' })),
        columnHelper.accessor('tradesCount', numberCol({ id: 'tradesCount', format: { fractionDigits: 0 } })),
        columnHelper.accessor('totalQuantity', numberCol({ id: 'totalQuantity', format: { fractionDigits: 0 } })),
        columnHelper.accessor('totalValue', moneyCol({ id: 'totalValue' })),
        columnHelper.display(excludeTradesCol(excludeTrades)),
    ];

    return (
        <TradesTable
            data={data}
            secondaryGrouping={secondaryGrouping}
            columns={columns}
            excludeTrades={excludeTrades}
            viewAsset={viewAsset}
            viewPortfolio={viewPortfolio}
            viewTrade={viewTrade}
            viewAdvanced={viewAdvanced}
        />
    );
};
